import { useState, useEffect, useCallback } from "react";
import { Platform, Alert, BackHandler } from "react-native";
import { Source } from "react-native-pdf";
import Config from "react-native-config";
import { getPDF } from "@lib";
import Constants, { getCarrierName } from "@lib/utilities/Constants";
import Storage from "@lib/utilities/storage/StorageFunctions";

import { DownloadUsingManager, Logger } from "@lib/utilities/HelperFunctions";
import { DocumentItem } from "./DocumentParser";
import { clearSessionAndLogout } from "@lib/utilities/AuthHelper";
import { useAuth0 } from "@auth0/auth0-react";
import { useAppSelector } from "@lib";
import Crashlytics from "@lib/utilities/crashlyticsUtils/CrashlyticsUtils";
import { PDF_VIEWER_ROUTE } from "../../../src/navigation/NavigationConstants";
import { messages } from "../../../src/resources/strings";
import RNFS from "../../../src/components/RNFS/RNFS";
import { documentNameFormatter } from "@lib/utilities/Formatters";
import { useDispatch } from "react-redux";
import { setError } from "@lib";

interface UsePDFViewerProps {
  document: DocumentItem;
  navigation: any;
}

export const usePDFViewer = ({ document, navigation }: UsePDFViewerProps) => {
  const [isLoading, setLoading] = useState(true);
  const [errorHandle, setErrorHandle] = useState(messages.error.noError);
  const [pdfSource, setPdfSource] = useState<Source>();
  const [isModalVisible, setModalVisible] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [downloadLoader, setDownloadLoader] = useState(false);
  const dispatch = useDispatch()
  
  const { logout } = useAuth0();
  const RETRY_LIMIT = 2;
  const isConnected = useAppSelector(
    (state) => state?.userReducer?.isConnected
  );

  // prevent hardware backpress during loading and document download
  useEffect(() => {
    if(Platform.OS=='android') {
      const onBackPress = () => {
        return isLoading;
      };
      const sub = BackHandler.addEventListener("hardwareBackPress", onBackPress);
      return () => sub.remove();
    } else {
      navigation.setOptions({
        gestureEnabled: !isLoading,
      });
    }
  }, [isLoading]);

  const handleInvalidTokenCase = () => {
    if (isConnected == null || isConnected) {
      clearSessionAndLogout(navigation, logout);
    } else {
      showInternetalert();
    }
  };

  const showInternetalert = async () => {
    setErrorHandle(messages.error.noError);
    setTimeout(() => {
      setErrorHandle(messages.error.youAreOffline);
    }, 10);
  };

  const clearCache = useCallback(async (path: string) => {
    try {
      // @ts-ignore
      await RNFS?.unlink(path);
      console.log("Clean cache success!" + path);
    } catch (error) {
      console.error("Error clean cache:", error);
    }
  }, []);

  const toggleModal = useCallback(() => {
    setModalVisible((prev) => !prev);
  }, []);

  const handleLoadComplete = useCallback(
    (numberOfPages: number, path: string) => {
      Logger(`Number of pages: ${numberOfPages}`);
      clearCache(path);
    },
    [clearCache]
  );

  const handlePageChanged = useCallback((page: number) => {  
    setLoading(false)
    Logger(`methodCurrent page: ${page}`);
  }, []);

  const handleError = useCallback((error: any) => {
    Logger(`PDF viewer error: ${error}`);
    if (Platform.OS == "ios") {
      retryPdf();
    } else {
      navigation.goBack()
      dispatch(setError(`${messages.error.pdfError}: ${documentNameFormatter(document?.fileName)}`))
      setErrorHandle(messages.error.pdfError?.replace(/pdf\..*$/i, `: ${documentNameFormatter(document?.fileName)}`))
    }
  }, []);
  const retryPdf = () => {
    if (document && retryCount < RETRY_LIMIT) {
      getPdfAPI(document);
      setRetryCount((prev) => prev + 1);
    } else setErrorHandle(messages.error.pdfError);
    return null;
  };

  const handleLinkPress = useCallback((uri: string) => {
    Logger(`Link pressed: ${uri}`);
  }, []);

  const getPdfSource = async (url: string) => {
    const accessToken = await Storage.getData(
      Constants.LocalStoreConstant.ACCESS_TOKEN
    );
    const source = {
      uri: url,
      cache: false,
      method: "GET",
      headers: {
        Authorization: accessToken,
        "Content-Type": "application/pdf",
      },
    };
    setPdfSource(source);
    // setLoading(false);
  };

  const getAndroidPdf = (document: any) => {
    const baseURL =
      Platform.OS === "web"
        ? process.env.REACT_APP_BASE_API_URL
        : Config.REACT_APP_BASE_API_URL;

    const requestUrl =
      `${baseURL}/policy/${document.policyNumber}` +
      `/file/${document.id}?policySource=${getCarrierName()}`;
    getPdfSource(requestUrl);
  };

  const getPdfAPI = async (document: DocumentItem) => {
    setLoading(true);
    const responseObj: any = await getPDF(document);

    if (responseObj.errorCode === messages.error.noError) {
      getPdfSource(responseObj.response);
    } else {
      setErrorHandle(responseObj.errorCode);
      navigation.goBack()
      setLoading(false);
      setTimeout(() => {
        dispatch(setError(`${messages.error.pdfError}: ${documentNameFormatter(document?.fileName)}`))
      }, 400)
    }
  };

  const downloadPdf = async () => {
    try {
      setLoading(true);
      setDownloadLoader(true)
      const baseURL =
        typeof (globalThis as any).document !== "undefined"
          ? process.env.REACT_APP_BASE_API_URL
          : Config.REACT_APP_BASE_API_URL;

      const requestUrl = `${baseURL}/policy/${document.policyNumber}/file/${
        document.id
      }?policySource=${getCarrierName()}`;

      const fileName = document.fileName || `document_${document.id}.pdf`;
      DownloadUsingManager(requestUrl, fileName, setErrorHandle, setLoading, setDownloadLoader)
    } catch (err) {
      console.error("DOWNLOAD ERROR:", err);
      setErrorHandle(messages.error.downloadFailedTryAgain)
    } finally {
      // setLoading(false);
    }
  };

  useEffect(() => {
    Crashlytics.logScreen(PDF_VIEWER_ROUTE);
    if (Platform.OS === "android") {
      getAndroidPdf(document);
    } else {
      getPdfAPI(document);
    }
  }, [document]);

  return {
    isLoading,
    errorHandle,
    pdfSource,
    setErrorHandle,
    isModalVisible,
    clearCache,
    toggleModal,
    handleLoadComplete,
    handlePageChanged,
    handleError,
    handleLinkPress,
    downloadPdf,
    handleInvalidTokenCase,
    downloadLoader
  };
};
